import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  BookOpen,
  Clock,
  Users,
  Star,
  Play,
  CheckCircle,
  ArrowLeft,
  Download,
  Share2,
  Heart,
  MessageCircle,
  Award,
  ChevronRight,
  User,
  Calendar,
  DollarSign,
  Globe,
  Lock,
  UserCheck,
  AlertCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { eCourseService } from "../../services/elearning/eCourseService";
import apiClient from "../../services/api";

interface CourseDetailProps {}

const CourseDetail: React.FC<CourseDetailProps> = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [enrollmentRequested, setEnrollmentRequested] = useState(false);

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customerFirstName, setCustomerFirstName] = useState("");
  const [customerLastName, setCustomerLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>("pending");
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);

  // Fetch course details
  const {
    data: courseData,
    isLoading: courseLoading,
    error: courseError,
  } = useQuery({
    queryKey: ["course-detail", courseId],
    queryFn: () => eCourseService.getPublicCourseDetails(courseId!),
    enabled: !!courseId,
    retry: false,
  });

  // Check if user is enrolled in this course
  const { data: enrollmentData, isLoading: enrollmentLoading } = useQuery({
    queryKey: ["course-enrollment", courseId],
    queryFn: () => eCourseService.getCourseEnrollmentStatus(courseId!),
    enabled: !!courseId,
  });

  // Enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: (courseId: string) => eCourseService.enrollInCourse(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["course-enrollment", courseId],
      });
      queryClient.invalidateQueries({ queryKey: ["student-enrolled-courses"] });
      setEnrollmentRequested(true);
    },
  });

  // Request enrollment mutation (for manual/approval required courses)
  const requestEnrollmentMutation = useMutation({
    mutationFn: (courseId: string) =>
      eCourseService.requestEnrollment(courseId),
    onSuccess: () => {
      setEnrollmentRequested(true);
    },
  });

  const course = courseData?.data;
  const enrollment = enrollmentData?.data;

  const isEnrolled = !!enrollment;
  const canSelfEnroll =
    course?.registration?.type === "self" &&
    !course?.registration?.requiresApproval;
  const requiresApproval = course?.registration?.requiresApproval || false;

  // Countdown timer for successful payment redirect - MUST be before any conditional returns
  useEffect(() => {
    if (paymentStatus === "completed") {
      setRedirectCountdown(5);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [paymentStatus]);

  if (courseLoading || enrollmentLoading) {
    return <LoadingSpinner />;
  }

  if (courseError) {
    console.log("Course error:", courseError);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/60 p-8">
            <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Course Not Found
            </h1>
            <p className="text-slate-600 mb-6 leading-relaxed">
              {courseError.message ||
                "The course you're looking for doesn't exist or has been removed."}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/60 p-8">
            <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Course Not Found
            </h1>
            <p className="text-slate-600 mb-6 leading-relaxed">
              The course you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleEnrollment = () => {
    // Check if it's a paid course
    if (course.pricing.type === "paid") {
      // Show payment modal for paid courses
      setShowPaymentModal(true);
      return;
    }

    // For free courses, proceed with enrollment
    if (canSelfEnroll) {
      enrollMutation.mutate(course._id);
    } else {
      requestEnrollmentMutation.mutate(course._id);
    }
  };

  // Payment handling functions (copied from StudentEcourseCatalog)
  const checkPaymentStatus = async (paymentId: string) => {
    try {
      const response = await apiClient.get(
        `/fees/payments/${paymentId}/status`
      );
      return (response as any).data;
    } catch (error) {
      console.error("❌ Error checking payment status:", error);
      return null;
    }
  };

  const startPaymentPolling = (paymentId: string) => {
    let pollCount = 0;
    const maxPolls = 100;

    const interval = setInterval(async () => {
      pollCount++;

      if (pollCount >= maxPolls) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setPaymentStatus("failed");
        toast.error(
          "Payment verification timeout. Please check your payment status."
        );
        return;
      }

      const payment = await checkPaymentStatus(paymentId);
      if (payment) {
        const isCompleted =
          payment.status === "completed" ||
          payment.verificationStatus === "verified";
        const isFailed =
          payment.status === "failed" ||
          payment.verificationStatus === "failed";
        const isCancelled =
          payment.status === "cancelled" ||
          payment.verificationStatus === "cancelled";

        if (isCompleted) {
          setPaymentStatus("completed");
          toast.success(
            "Payment successful! You are now enrolled in the course.",
            { duration: 5000 }
          );
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          queryClient.invalidateQueries({
            queryKey: ["course-enrollment", courseId],
          });
        } else if (isFailed) {
          setPaymentStatus("failed");
          toast.error("Payment failed. Please try again or contact support.", {
            duration: 5000,
          });
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else if (isCancelled) {
          setPaymentStatus("cancelled");
          toast.error("Payment was cancelled. You can try again when ready.", {
            duration: 5000,
          });
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else {
          setPaymentStatus(payment.status);
        }
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const handlePaymentSubmit = async () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!customerFirstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (!customerLastName.trim()) {
      toast.error("Please enter your last name");
      return;
    }
    if (!customerEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const response = await apiClient.post(
        `/elearning/courses/${course._id}/enroll`,
        {
          phoneNumber,
          customerFirstName,
          customerLastName,
          customerEmail,
        }
      );

      if ((response as any).success) {
        setPaymentInitiated(true);
        setPaymentData((response as any).data);
        setPaymentStatus("pending");

        toast.success(
          (response as any).message ||
            "Payment request sent! Check your phone for M-Pesa prompt.",
          { duration: 5000 }
        );

        const paymentId = (response as any).data?.paymentId;
        if (paymentId) {
          startPaymentPolling(paymentId);
        }
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to initiate payment. Please try again."
      );
    }
  };

  const closePaymentModal = () => {
    // If payment is completed, the countdown timer will handle the redirect
    if (paymentStatus === "completed") {
      return;
    }

    // If payment failed, reset payment state but keep modal open to retry
    if (paymentStatus === "failed") {
      setPaymentInitiated(false);
      setPaymentData(null);
      setPaymentStatus("pending");
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      return;
    }

    // For cancelled or other states, close modal completely
    setShowPaymentModal(false);
    setPhoneNumber("");
    setCustomerFirstName("");
    setCustomerLastName("");
    setCustomerEmail("");
    setPaymentInitiated(false);
    setPaymentData(null);
    setPaymentStatus("pending");

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    queryClient.invalidateQueries({
      queryKey: ["course-enrollment", courseId],
    });
  };

  const handleStartLearning = () => {
    if (enrollment && enrollment.progress > 0) {
      navigate(`/student/elearning/course/${course._id}/continue`);
    } else {
      navigate(`/student/elearning/course/${course._id}/start`);
    }
  };

  const renderStars = (rating: number = 0) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const getEnrollmentButton = () => {
    if (isEnrolled) {
      return (
        <button
          onClick={handleStartLearning}
          className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
        >
          <Play className="w-5 h-5" />
          {enrollment.progress > 0 ? "Continue Learning" : "Start Course"}
        </button>
      );
    }

    if (enrollmentRequested) {
      return (
        <div className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-md">
          <CheckCircle className="w-5 h-5" />
          {requiresApproval ? "Enrollment Requested" : "Enrolled Successfully"}
        </div>
      );
    }

    if (enrollMutation.isPending || requestEnrollmentMutation.isPending) {
      return (
        <button
          disabled
          className="w-full sm:w-auto px-8 py-3.5 bg-slate-300 text-white font-semibold rounded-xl flex items-center justify-center gap-2 opacity-60"
        >
          <LoadingSpinner />
          Processing...
        </button>
      );
    }

    return (
      <button
        onClick={handleEnrollment}
        className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
      >
        <UserCheck className="w-5 h-5" />
        {requiresApproval ? "Request Enrollment" : "Enroll Now"}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center rounded-2xl gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <div className="w-8 h-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center group-hover:shadow-lg transition-all duration-300">
              <ArrowLeft className="w-4 h-4 text-white" />
            </div>
            <span className="font-medium">Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Course Header */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-8 hover:shadow-lg transition-all duration-300">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-sm mb-3">
                  <span className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-xs font-semibold shadow-sm">
                    {course.category}
                  </span>
                  <span className="px-3 py-1.5 bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-full text-xs font-semibold shadow-sm">
                    {course.level}
                  </span>
                </div>
                <h1 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
                  {course.title}
                </h1>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {course.shortDescription || course.description}
                </p>

                {/* Course Meta */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {course.instructor.firstName} {course.instructor.lastName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {course.duration.estimatedHours}h
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Users className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {course.stats?.totalStudents || 0} students
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Globe className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">{course.language}</span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    {renderStars(course.rating?.average || 0)}
                    <span className="text-sm font-semibold text-slate-900 ml-1">
                      {course.rating?.average?.toFixed(1) || "0.0"}
                    </span>
                    <span className="text-sm text-slate-500">
                      ({course.rating?.count || 0} reviews)
                    </span>
                  </div>
                </div>

                {/* Progress Bar (if enrolled) */}
                {isEnrolled && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-700">
                        Your Progress
                      </span>
                      <span className="text-sm font-semibold text-blue-600">
                        {enrollment.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 shadow-inner">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-500 shadow-sm"
                        style={{ width: `${enrollment.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {getEnrollmentButton()}

                  <div className="flex gap-2">
                    <button className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:shadow-md transition-all duration-300 group">
                      <Heart className="w-5 h-5 text-slate-600 group-hover:text-red-500 transition-colors" />
                    </button>
                    <button className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:shadow-md transition-all duration-300 group">
                      <Share2 className="w-5 h-5 text-slate-600 group-hover:text-blue-500 transition-colors" />
                    </button>
                    <button className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50 hover:shadow-md transition-all duration-300 group">
                      <Download className="w-5 h-5 text-slate-600 group-hover:text-green-500 transition-colors" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Description */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-8 hover:shadow-lg transition-all duration-300">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                About This Course
              </h2>
              <div className="prose max-w-none text-slate-600 leading-relaxed">
                <p>{course.description}</p>
                {course.chain && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Learning Path Certificate
                    </h3>
                    <p className="text-blue-700 leading-relaxed">
                      This course is part of a learning path. Complete all
                      courses in this chain to earn your certificate at the end.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Course Content Preview (if not enrolled) */}
            {!isEnrolled && (
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-8 hover:shadow-lg transition-all duration-300">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  Course Content
                </h2>
                <div className="space-y-3">
                  {/* Mock course content - this would come from actual curriculum */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-300 group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-sm">
                        <span className="text-sm font-bold text-white">1</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                          Introduction
                        </h3>
                        <p className="text-sm text-slate-500">
                          Getting started with the basics
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span>15 min</span>
                      </div>
                      <Lock className="w-4 h-4" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-all duration-300 opacity-60">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                        <span className="text-sm font-bold text-slate-600">
                          2
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-600">
                          Core Concepts
                        </h3>
                        <p className="text-sm text-slate-400">
                          Understanding the fundamentals
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span>45 min</span>
                      </div>
                      <Lock className="w-4 h-4" />
                    </div>
                  </div>

                  <div className="text-center py-4">
                    <p className="text-sm font-medium text-slate-500 bg-slate-50 rounded-xl py-3 px-4 inline-block">
                      + 8 more lessons
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Course Info Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                  <Award className="w-4 h-4 text-white" />
                </div>
                Course Information
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-600">
                    Price
                  </span>
                  <span className="font-bold text-slate-900">
                    {course.pricing.type === "free"
                      ? "Free"
                      : `$${course.pricing.amount}`}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-600">
                    Duration
                  </span>
                  <span className="font-bold text-slate-900">
                    {course.duration.estimatedHours}h{" "}
                    {course.duration.estimatedMinutes}m
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-600">
                    Level
                  </span>
                  <span className="font-bold text-slate-900">
                    {course.level}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-600">
                    Language
                  </span>
                  <span className="font-bold text-slate-900">
                    {course.language}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-600">
                    Enrollment
                  </span>
                  <span className="font-bold text-slate-900">
                    {course.registration.type === "self"
                      ? "Open"
                      : course.registration.type === "manual"
                      ? "Manual"
                      : "Invite Only"}
                  </span>
                </div>

                {course.registration.maxStudents && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-slate-600">
                      Max Students
                    </span>
                    <span className="font-bold text-slate-900">
                      {course.registration.maxStudents}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Instructor Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                Instructor
              </h3>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md overflow-hidden relative">
                  {(course.instructor as any).profilePicture ||
                  (course.instructor as any).profileDetails?.profilePicture ? (
                    <img
                      src={
                        (course.instructor as any).profilePicture ||
                        (course.instructor as any).profileDetails?.profilePicture
                      }
                      alt={`${course.instructor.firstName} ${course.instructor.lastName}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback instanceof HTMLElement) {
                          fallback.classList.remove("hidden");
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`${
                      (course.instructor as any).profilePicture ||
                      (course.instructor as any).profileDetails?.profilePicture
                        ? "hidden"
                        : ""
                    } absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center`}
                  >
                    <User className="w-7 h-7 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">
                    {course.instructor.firstName} {course.instructor.lastName}
                  </h4>
                  <p className="text-sm text-slate-500">
                    {course.instructor.email}
                  </p>
                </div>
              </div>

              <button className="w-full px-4 py-3 border border-slate-200 rounded-xl hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:text-white hover:border-transparent transition-all duration-300 flex items-center justify-center gap-2 font-medium group">
                <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                Contact Instructor
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Pay with Equity Bank
            </h3>

            {paymentInitiated ? (
              <div className="text-center">
                {paymentStatus === "pending" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <h4 className="text-xl font-bold text-blue-800 mb-3">
                      M-Pesa Payment Request Sent
                    </h4>
                    <p className="text-blue-700 mb-4 text-base">
                      Check your phone for the M-Pesa payment prompt and enter
                      your PIN to complete the payment.
                    </p>
                    {paymentData && (
                      <div className="bg-white rounded-lg p-4 space-y-2 text-left border border-blue-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Course:
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {paymentData.courseTitle}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Amount:
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            KES {paymentData.amount}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Charge:
                          </span>
                          <span className="text-sm font-bold text-slate-700">
                            KES {paymentData.charge}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-t pt-2">
                          <span className="text-sm font-semibold text-slate-600">
                            Total Debited:
                          </span>
                          <span className="text-base font-bold text-blue-700">
                            KES {paymentData.amountDebited}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Phone:
                          </span>
                          <span className="text-sm font-mono text-slate-900">
                            {paymentData.phoneNumber}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Reference:
                          </span>
                          <span className="text-xs font-mono text-slate-700">
                            {paymentData.orderReference}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {paymentStatus === "completed" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold text-green-800 mb-3">
                      Payment Successful!
                    </h4>
                    <p className="text-green-700 mb-4 text-base">
                      Your M-Pesa payment has been confirmed and you have been
                      successfully enrolled in the course.
                    </p>
                    <div className="bg-white rounded-lg p-4 mb-4 border border-green-200">
                      <div className="flex items-center justify-center text-green-700">
                        <Clock className="w-5 h-5 mr-2" />
                        <span className="text-sm font-semibold">
                          Redirecting to course in {redirectCountdown} second
                          {redirectCountdown !== 1 ? "s" : ""}...
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {paymentStatus === "failed" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold text-red-800 mb-3">
                      Payment Failed
                    </h4>
                    <p className="text-red-700 mb-4 text-base">
                      The M-Pesa payment could not be completed. Please try
                      again.
                    </p>
                  </div>
                )}

                {paymentStatus === "cancelled" && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold text-yellow-800 mb-3">
                      Payment Cancelled
                    </h4>
                    <p className="text-yellow-700 mb-4 text-base">
                      The M-Pesa payment request was cancelled.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    if (paymentStatus === "completed") {
                      // Immediately redirect when button is clicked
                      window.location.reload();
                    } else {
                      closePaymentModal();
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {paymentStatus === "completed"
                    ? "Start Learning Now"
                    : paymentStatus === "failed"
                    ? "Try Again"
                    : "Close"}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">
                    {course.title}
                  </h4>
                  <p className="text-3xl font-bold text-slate-900 mb-3">
                    KES {course.pricing.amount}
                  </p>
                  <p className="text-sm text-slate-600">
                    Enter your details for Equity payment
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerFirstName}
                      onChange={(e) => setCustomerFirstName(e.target.value)}
                      placeholder="John"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerLastName}
                      onChange={(e) => setCustomerLastName(e.target.value)}
                      placeholder="Doe"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="john.doe@example.com"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="254XXXXXXXXX"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 bg-slate-100 text-slate-700 py-3 px-4 rounded-xl hover:bg-slate-200 font-semibold transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePaymentSubmit}
                    disabled={
                      !phoneNumber.trim() ||
                      !customerFirstName.trim() ||
                      !customerLastName.trim() ||
                      !customerEmail.trim()
                    }
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    Pay & Enroll
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
